import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'

export type UseFeatureFlagsLoginParams = {
  /**
   * The identifying key that identifies a user in our systems, such as a user ID.
   */
  key: string
  /**
   * The user's first name.
   */
  firstName?: string
  /**
   * The user's last name.
   */
  lastName?: string
  /**
   * The user's primary email address.
   */
  email?: string
  /**
   * Custom properties
   */
  custom?: Record<string, string>
}

export type TopBannerAlertFlag = {
  enabled: boolean
  type: 'info' | 'warn'
  message: string
  ctaLabel?: string
  ctaUrl?: string
}

export type AppEngineConfig = {
  /**
   * Controls if the browser should periodically refresh.
   */
  refreshEnabled: boolean
  /**
   * The rate at which the browser should poll for new data, in milliseconds.
   */
  refreshRateMS: number
}

export type UseFeatureFlags = {
  /**
   * An object containing all feature flags used by the application.
   */
  flags: {
    /**
     * Controls visibility of the activity log tab in the application detail page.
     */
    ['admin.app-engine-activity-log-enabled']: boolean
    /**
     * Controls visibility of the QLE v1 feature.
     */
    ['qle-v-1']: boolean
    /**
     * Controls visibility of the Zendesk widget.
     */
    ['customer-service.fe.zendesk-widget-enabled']: boolean
    /**
     * Controls visibility of some HR reports
     */
    ['product.enrollme.q4-hr-reports-enabled']: boolean
    /**
     * Controls visibility of reports tab
     */
    ['admin.show-reports-tab-to-hr-users']: boolean
    /**
     * Controls visibility of enrollment dashboard
     */
    ['admin.show-enrollment-dashboard']: boolean
    /**
     * Controls visibility of the Censory tab
     */
    ['admin.is-censory-enabled']: boolean
    /**
     * Controls visibility of the Our People > Send Email action button
     */
    ['our-people-send-email-action']: boolean
    /**
     * Controls visibility of the select-all and individual checkboxes on the enrollment dashboard.
     */
    ['engineering.hr-portal.employee-list-checkboxes-enabled']: boolean
    /**
     * Controls visibility of new Payroll HRIS Sync History
     */
    ['product.admin.payroll-sync-enabled']: boolean
    /**
     * Controls if address should be verification by calling API
     */
    ['feature-address-verification']: boolean
    /**
     * Controls usage of new Termination flow
     */
    ['product.termination.new-flow']: boolean
    /**
     * Configuration of App Engine refresh page. Can enable/disable browser polling, and rate in milliseconds.
     */
    ['product.admin.appengine.config']: AppEngineConfig
    /**
     * Controls if the census tab is available in company profile page
     */
    ['admin.hris.tab-available']: boolean
    /**
     * Controls if the payroll tab is available in company profile page
     */
    ['admin.payroll.tab-available']: boolean
    /**
     * Controls if the activity log tab is available in employee profile page
     */
    ['admin.enable-employee-activity-log-tab']: boolean
    /**
     * Controls if the activity log tab is available in company profile page
     */
    ['admin.enable-company-activity-log-tab']: boolean
    /**
     * Controls if the expandable activity log view is enabled
     */
    ['admin.enable-expandable-activity-log']: boolean
    /**
     * Only effective when user is a broker role. Controls if the broker user has access
     * to "Login as Worker" action
     */
    ['broker.impersonate-as-user.ui']: boolean
    /**
     * Controls user who can't see salary information
     */
    ['admin.salary.unavailable']: boolean
    /**
     * Temp flag to control if the impersonate url point to new domain
     */
    ['temp.ui.admin.new-domain']: boolean
    /**
     * Flag to control if ACH Account Number fields are displayed in app engine / payment info tab
     */
    ['admin.ui.ach-acct']: boolean
    /**
     * The same LaunchDarkly key partner-benefit-graph gates its Kaiser autopay path on, read here
     * so the Application Integration Submission section can say whether submitting will create an
     * ACI schedule. Optional on purpose: a flag not published to client-side SDKs is absent rather
     * than false, and reporting that absence as "flag off" would claim no schedule will be created
     * on every Kaiser submission. Undefined means unknown — say nothing about the flag.
     */
    ['service.partner-benefit-graph.sync.kaiser.autopay.enabled']?: boolean
    /**
     * Temp flag to control citizenship questions for employee form
     */
    ['temp.ui.citizenship-question']: boolean
    /**
     * Temp flag to allow access to the new Ancillary report
     */
    ['temp.ui.admin.enable-ancillary-report']: boolean
    /**
     * Temp flag to allow access to the new 1095-C report
     */
    ['temp.ui.admin.enable-1095c-report']: boolean
    /**
     * Temp flag to allow access to the medicare report for HR admins
     */
    ['temp.ui.admin.enable-medicare-report']: boolean
    /**
     * Temp flag to allow access to the new benefit effective report
     */
    ['temp.ui.admin.enable-new-benefit-effective-report']: boolean
    /**
     * Used to provide OEP end time for enrollment dashboard
     */
    ['enrollment.dashboard.endtime.ui']: string
    /**
     * EMS-10444 — Controls visibility of the "Waive" and "Upload waive benefit"
     * buttons on the enrollment dashboard. When false (default), both are hidden.
     */
    ['engineering.hr-portal.waive-benefit-enabled']: boolean
    /**
     * Flag to control if the new customer module will be available in the UI
     */
    ['ui.customer.new-experience']: boolean

    /**
     * List of client ids for which census V2 is available
     */
    ['ui.admin.enable-census-v2-for-client']: string[]
    /**
     * Used to display the activity log popup reason.
     */
    ['admin.activity-log-reason-popup-enabled']: boolean
    /**
     * Controls if the new company contact detail feature is enabled
     */
    ['admin.enable-new-company-contact-detail']: boolean
    /**
     * Controls if the two version assignee list is enabled
     */
    ['admin.is-show-two-version-assignee-enabled']: boolean
    /**
     * Allowed clients for payroll custom report
     */
    ['admin.payroll.custom-report.allowed-client-public-keys']: string[]
    /**
     * List of states for which the organ donor question is eligible
     */
    ['admin.eligible-states-for-organ-donor-question']: string[]
    /**
     * List of allowed carrier names for which the Payment Bot feature is available
     */
    ['admin.payment-bot.allowed-carriers']: string[]
    /**
     * Controls if the new customer onboarding module is enabled
     */
    ['admin.new-customer-onboarding-enabled']: boolean
    /**
     * Controls if the EE Docs Request section is shown on the application detail page
     */
    ['admin.ee-docs-request-enabled']: boolean
    /**
     * Controls if the System Documents section is shown on the application detail page
     */
    ['admin.system-file-generated-enabled']: boolean
    /**
     * Controls if split entity census upload is enabled
     */
    ['admin.ama-census-split-entity-enabled']: boolean
    /**
     * VCN provider safety switch — WEX. Set false during a WEX outage to stop
     * new WEX card creation; existing cards, terminate and reuse are unaffected.
     * Reused key: it previously routed create between providers (EMS-9720).
     */
    ['admin.virtual-card.use-wex-vcn']: boolean
    /**
     * VCN provider safety switch — Marqeta. Set false during a Marqeta outage to
     * stop new Marqeta card creation; existing cards are unaffected.
     */
    ['admin.virtual-card.use-marqeta-vcn']: boolean
    /**
     * When true, shows activity log modal (Reason Category, Reason, Zendesk Ticket)
     * before generating a new VCN. When false, shows simple reason input.
     */
    ['admin.virtual-card.enable-note-modal-before-create']: boolean
    /**
     * When true, terminates all active cards for the employee before generating
     * a new VCN. Works in conjunction with enable-note-modal-before-create.
     */
    ['admin.virtual-card.enable-terminate-all-before-create']: boolean
    /**
     * When true, shows activity log modal (Reason Category, Reason, Zendesk Ticket)
     * before generating a new VCN in the legacy Our People section.
     */
    ['admin.virtual-card.enable-note-modal-before-create-our-people']: boolean
    /**
     * When true, terminates all active cards for the employee before generating
     * a new VCN in the legacy Our People section.
     */
    ['admin.virtual-card.enable-terminate-all-before-create-our-people']: boolean
    /**
     * When true, the Application Engine download uses the NDJSON streaming export endpoint.
     * Set to false to fall back to the single-request paginated endpoint.
     */
    ['admin.app-engine.use-streaming-export']: boolean
    /**
     * When true, the Application Engine v1 sidebar entry is shown.
     * Set to false to hide the v1 menu. Direct URL access is still allowed.
     */
    ['admin.app-engine.v1-enabled']: boolean
    /**
     * Controls visibility/access for ABP5 Open Enrollment workspace.
     * When true, the Open Enrollment sidebar entry and ABP5 Open Enrollment workspace access are enabled.
     */
    ['admin.abp-5.open-enrollment']: boolean
    /**
     * Controls visibility of the Open Enrollment Approvals tab.
     * When true, the Approvals tab is shown; when false, it is hidden for all
     * Open Enrollment roles (platform admin, client admin, broker).
     */
    ['admin.abp-5.show-approval-tab']: boolean
    /**
     * Controls visibility of the "Total Cost" section on the Open Enrollment
     * Policy/Summary tab. When true, the Total Cost card is shown; when
     * false (default), it is hidden.
     */
    ['admin.abp-5.show-total-cost']: boolean
    /**
     * EMS-10086 — Gates the temporary /abp/dashboard master-file upload admin
     * page. Intended to be targeted by email (not a blanket rollout) so only
     * specific testers can reach this page; fail-safe default is OFF. Remove
     * this flag + gate once the temporary page is retired.
     */
    ['admin.abp-5.show-dashboard']: boolean
    /**
     * Controls visibility of the "Generate target file" section on the
     * View Customer Open Enrollment tab. When false (default), it is hidden.
     */
    ['admin.abp-5.show-generate-target-file']: boolean
    /**
     * EMS-10557 — Controls visibility of the "Benefit effective date" period
     * drop-down in the ABP Open Enrollment page header. When false (default),
     * it is hidden and the page shows only the default enrollment period.
     */
    ['admin.abp-5.show-open-enrollment-toggle']: boolean
    /**
     * EMS-10336 / EMS-10863 — Controls visibility of the "ABP 5 File"
     * column (per-row generate/download) in the analysis upload table on
     * the View Customer Open Enrollment tab. When false (default), the
     * column is hidden and abp5-file job queries are not issued.
     */
    ['admin.abp-5.show-generate-abp5-file']: boolean
    /**
     * EMS-10185 — Controls visibility of the entire "Analysis Upload" section
     * (OepXlsxUploadSection) on the View Customer Open Enrollment tab. When
     * false (default), the whole section is hidden.
     */
    ['admin.abp.allow-analysis-file-upload']: boolean
    /**
     * Controls visibility of the "Delete" action (in the row actions dropdown)
     * for analysis files on the View Customer Open Enrollment tab. When false
     * (default), the dropdown is hidden entirely.
     */
    ['admin.abp.enable-delete-analysis-file']: boolean
    /**
     * Controls which download options appear in the Quoting download dropdown.
     * When true, only LC run download options are shown; when false, only legacy download options.
     */
    ['admin.eqts-download-one-prospect-enabled']: boolean
    /**
     * Controls visibility of the "Upload 2 Files" button on the EQTS census upload view.
     */
    ['admin.eqts-upload-one-prospect-enabled']: boolean
    /**
     * Controls whether the EQTS multi-run Plans & Rates table/experience is shown.
     */
    ['admin.eqts-multiple-run-table-enabled']: boolean
    /**
     * Controls the platform-wide alternate customer support phone banner.
     * Controls the platform-wide top banner alert (notices, warnings, alerts).
     */
    ['platform.top-banner-alert']: TopBannerAlertFlag
    /**
     * Controls visibility of the Generation mode tab on the
     * Finance > Invoice Management page. When off, only Delivery is available.
     */
    ['admin.finance.invoice-generation-enabled']: boolean
    /**
     * Controls visibility/access of the Finance > Users (user invitations) feature.
     * When off, the /finance/users route redirects away and invite entry points are hidden.
     */
    ['admin.finance.user-invitations-enabled']: boolean
    /**
     * Controls visibility/access of the Finance > Payment Reconciliation (v2)
     * feature. When off, the /finance/payment-reconciliation-v2 route redirects
     * away and the Finance landing tile is hidden.
     */
    ['admin.finance.reconciliation-v2-enabled']: boolean
    /**
     * Controls visibility of the Finance > Reconciliation Reports tile
     * (monthly + annual summary). When off, the landing tile is hidden.
     */
    ['admin.finance.reconciliation-v2-reports-enabled']: boolean
    /**
     * Controls whether the "Check" option appears in the Primary Payment Method
     * dropdown on the Payment Assignment detail modal.
     */
    ['admin.finance.payment-coordination-enable-check']: boolean
    /**
     * Controls whether the "GROUP ACH" option appears in the Primary Payment
     * Method dropdown on the Payment Assignment detail modal.
     */
    ['admin.finance.payment-coordination-enable-group-ach']: boolean
    /**
     * Controls whether Finance > Invoice Management's Delivery mode surfaces
     * the Stripe payment view (Draw status/actions, and the drawer's Stripe
     * Payments section) instead of the plain delivery-only view. When on,
     * the `delivery` URL is redirected to `delivery-and-payment`.
     */
    ['admin.finance.stripe-integration-enabled']: boolean
    /**
     * Controls whether AP_CONFIRMED, PAYMENT_CLEARED, and BOT_CLEARED statuses
     * are available in the single-case status update dropdown and the bulk
     * status update CSV page. When off, those three "clear-by-manual" statuses
     * are hidden from all status selectors.
     */
    ['admin.finance.payment-coordination-enable-clear-by-manual']: boolean
    /**
     * Client-side kill-switch for enrollment-platform RBAC enforcement. When
     * off, the RBAC gates are a no-op and surfaces fall back to their coarse
     * role checks. Mirror of the EPS `service.rbac-enforcement-enabled` flag.
     */
    ['admin.rbac-enforcement-enabled']: boolean
    /**
     * Controls visibility of the Create Demo Customer button and access to the
     * demo customer create and view pages.
     */
    ['admin.customers.demo-management-enabled']: boolean
    /**
     * Controls visibility of the "Plan Benefits CSV" download/upload button
     * on the Plan Information (/plans-v2) page.
     */
    ['admin.plan-management-plan-benefits-csv-enabled']: boolean
    /**
     * Controls visibility of the Upload tab inside the "Plan Benefits CSV"
     * modal — gated separately from the modal itself since Upload mutates
     * live data and Download doesn't.
     */
    ['admin.plan-management-plan-benefits-csv-upload-enabled']: boolean
    /**
     * Controls whether client HR admins (PlatformClientAdmin) can access the
     * Document Center. When on, HR admins get read-only access to the
     * Post-Enrollment and Medicare tabs only — the Medicare Age-In tab stays
     * hixme-admin only, and the employee-name link to Application Engine is
     * disabled for this role.
     */
    ['admin.show-document-center-to-hr-admins']: boolean
    /**
     * Controls whether the "Upload" button on the Health Plan tab
     * (Plan Management V3) is enabled — disabled (not hidden) when off,
     * since upload mutates live data. Mirrors
     * admin.plan-management-plan-benefits-csv-upload-enabled on the
     * Plan Information (/plans-v2) page.
     */
    ['admin.plan-management-v3-health-plan-upload-enabled']: boolean
  }
  /**
   * Callback methods used to perform actions on our feature flag storage, such as identifying a user after login.
   */
  methods: {
    /**
     * Identifies a user after a successful login.
     */
    identify: (params: UseFeatureFlagsLoginParams) => void
  }
}

const useFeatureFlags = (): UseFeatureFlags => {
  try {
    const flags = useFlags()
    const ldClient = useLDClient()

    const identify = (data: UseFeatureFlagsLoginParams) => {
      ldClient
        ?.identify(data)
        .catch((e) => console.error('LaunchDarkly - Error identifying user', e))
    }

    return { flags, methods: { identify } } as UseFeatureFlags
  } catch (e) {
    console.error('Error initializing LaunchDarkly', e)
    return defaultFeatureFlags
  }
}

export default useFeatureFlags

const defaultFeatureFlags: UseFeatureFlags = {
  /**
   * An object containing all feature flags used by the application.
   */
  flags: {
    /**
     * Controls visibility of the activity log tab in the application detail page.
     */
    'admin.app-engine-activity-log-enabled': false,
    /**
     * Controls visibility of the QLE v1 feature.
     */
    'qle-v-1': false,
    /**
     * Controls visibility of the Zendesk widget.
     */
    'customer-service.fe.zendesk-widget-enabled': false,
    /**
     * Controls visibility of some HR reports
     */
    'product.enrollme.q4-hr-reports-enabled': false,
    /**
     * Controls visibility of reports tab
     */
    'admin.show-reports-tab-to-hr-users': false,
    /**
     * Controls visibility of enrollment dashboard
     */
    'admin.show-enrollment-dashboard': false,
    /**
     * Controls visibility of the Censory tab
     */
    'admin.is-censory-enabled': false,
    /**
     * Controls visibility of the Our People > Send Email action button
     */
    'our-people-send-email-action': false,
    /**
     * Controls visibility of the select-all and individual checkboxes on the enrollment dashboard.
     */
    'engineering.hr-portal.employee-list-checkboxes-enabled': false,
    /**
     * Controls visibility of new Payroll HRIS Sync History
     */
    'product.admin.payroll-sync-enabled': false,
    /**
     * Controls if address should be verification by calling API
     */
    'feature-address-verification': false,
    /**
     * Controls usage of new Termination flow
     */
    'product.termination.new-flow': false,
    /**
     * Configuration of App Engine refresh page. Can enable/disable browser polling, and rate in milliseconds.
     */
    'product.admin.appengine.config': {
      refreshEnabled: false,
      refreshRateMS: 10000,
    },
    /**
     * Controls if the census tab is available in company profile page
     */
    'admin.hris.tab-available': false,
    /**
     * Controls if the payroll tab is available in company profile page
     */
    'admin.payroll.tab-available': false,
    /**
     * Controls if the activity log tab is available in employee profile page
     */
    'admin.enable-employee-activity-log-tab': false,
    /**
     * Controls if the activity log tab is available in company profile page
     */
    'admin.enable-company-activity-log-tab': false,
    /**
     * Controls if the expandable activity log view is enabled
     */
    'admin.enable-expandable-activity-log': false,
    /**
     * Only effective when user is a broker role. Controls if the broker user has access
     * to "Login as Worker" action
     */
    'broker.impersonate-as-user.ui': false,
    /**
     * Controls user who can't see salary information
     */
    'admin.salary.unavailable': false,
    /**
     * Temp flag to control if the impersonate url point to new domain
     */
    'temp.ui.admin.new-domain': false,
    /**
     * Flag to control if ACH Account Number fields are displayed in app engine / payment info tab
     */
    'admin.ui.ach-acct': false,
    'temp.ui.citizenship-question': false,

    // Report flags
    'temp.ui.admin.enable-ancillary-report': false,
    'temp.ui.admin.enable-1095c-report': false,
    'temp.ui.admin.enable-medicare-report': false,
    'temp.ui.admin.enable-new-benefit-effective-report': false,

    /**
     * Used to provide OEP end time for enrollment dashboard
     */
    'enrollment.dashboard.endtime.ui': '00:00',
    /**
     * Controls visibility of the waive benefit buttons on the enrollment dashboard.
     */
    'engineering.hr-portal.waive-benefit-enabled': false,
    /**
     * Flag to control if the new customer module will be available in the UI
     */
    'ui.customer.new-experience': false,

    /**
     * List of client ids for which census V2 is available
     */
    'ui.admin.enable-census-v2-for-client': [],
    /**
     * Used to display the activity log popup reason.
     */
    'admin.activity-log-reason-popup-enabled': false,
    /**
     * Controls if the new company contact detail feature is enabled
     */
    'admin.enable-new-company-contact-detail': false,
    /**
     * Controls if the two version assignee list is enabled
     */
    'admin.is-show-two-version-assignee-enabled': false,
    /**
     * Allowed clients for payroll custom report
     */
    'admin.payroll.custom-report.allowed-client-public-keys': [],
    /**
     * List of states for which the organ donor question is eligible
     */
    'admin.eligible-states-for-organ-donor-question': [],
    /**
     * List of allowed carrier names for which the Payment Bot feature is available
     */
    'admin.payment-bot.allowed-carriers': [],
    /**
     * Controls if the new customer onboarding module is enabled
     */
    'admin.new-customer-onboarding-enabled': false,
    /**
     * Controls if the EE Docs Request section is shown on the application detail page
     */
    'admin.ee-docs-request-enabled': false,
    /**
     * Controls if the System Documents section is shown on the application detail page
     */
    'admin.system-file-generated-enabled': false,
    /**
     * Controls if split entity census upload is enabled
     */
    'admin.ama-census-split-entity-enabled': false,
    // Fail-OPEN defaults — LaunchDarkly being unreachable is not a provider
    // outage, so card creation must keep working when flags can't be read.
    'admin.virtual-card.use-wex-vcn': true,
    'admin.virtual-card.use-marqeta-vcn': true,
    // VCN modal flags — default OFF, enable via LaunchDarkly
    'admin.virtual-card.enable-note-modal-before-create': false,
    'admin.virtual-card.enable-terminate-all-before-create': false,
    // VCN modal flags for legacy Our People section — default OFF
    'admin.virtual-card.enable-note-modal-before-create-our-people': false,
    'admin.virtual-card.enable-terminate-all-before-create-our-people': false,
    /**
     * When true, the Application Engine download uses the NDJSON streaming export endpoint.
     * Set to false to fall back to the single-request paginated endpoint.
     */
    'admin.app-engine.use-streaming-export': true,
    'admin.app-engine.v1-enabled': true,
    // EMS-10086 — fail-safe default OFF for the temporary email-targeted dashboard gate.
    'admin.abp-5.show-dashboard': false,
    'admin.abp-5.open-enrollment': false,
    // Fail-safe default OFF — Approvals tab hidden when LaunchDarkly is unavailable.
    'admin.abp-5.show-approval-tab': false,
    // Fail-safe default OFF — Total Cost section hidden when LaunchDarkly is unavailable.
    'admin.abp-5.show-total-cost': false,
    // Fail-safe default OFF — Generate target file section hidden when LaunchDarkly is unavailable.
    'admin.abp-5.show-generate-target-file': false,
    // EMS-10557 — fail-safe default OFF: period drop-down hidden when LaunchDarkly is unavailable.
    'admin.abp-5.show-open-enrollment-toggle': false,
    // Fail-safe default OFF — ABP 5 File column hidden when LaunchDarkly is unavailable.
    'admin.abp-5.show-generate-abp5-file': false,
    // Fail-safe default OFF — Analysis Upload section hidden when LaunchDarkly is unavailable.
    'admin.abp.allow-analysis-file-upload': false,
    // Fail-safe default OFF — Delete is a destructive action, hidden when LaunchDarkly is unavailable.
    'admin.abp.enable-delete-analysis-file': false,
    /**
     * Controls which download options appear in the Quoting download dropdown.
     * When true, only LC run download options are shown; when false, only legacy download options.
     */
    'admin.eqts-download-one-prospect-enabled': false,
    /**
     * Controls visibility of the "Upload 2 Files" button on the EQTS census upload view.
     */
    'admin.eqts-upload-one-prospect-enabled': false,
    /**
     * Controls whether the EQTS multi-run Plans & Rates table/experience is shown.
     */
    'admin.eqts-multiple-run-table-enabled': false,
    /**
     * Controls the platform-wide top banner alert (notices, warnings, alerts).
     */
    'platform.top-banner-alert': {
      enabled: false,
      type: 'info',
      message: '',
    },
    /**
     * Controls visibility of the Generation mode tab on the
     * Finance > Invoice Management page. When off, only Delivery is available.
     */
    'admin.finance.invoice-generation-enabled': false,
    /**
     * Controls visibility/access of the Finance > Users (user invitations) feature.
     * When off, the /finance/users route redirects away and invite entry points are hidden.
     */
    'admin.finance.user-invitations-enabled': false,
    /**
     * Controls visibility/access of the Finance > Payment Reconciliation (v2)
     * feature. Fail-safe default OFF — the route redirects away and the landing
     * tile is hidden until LaunchDarkly turns it on.
     */
    'admin.finance.reconciliation-v2-enabled': false,
    /**
     * Fail-safe default OFF — the Reconciliation Reports tile is hidden until
     * LaunchDarkly turns it on.
     */
    'admin.finance.reconciliation-v2-reports-enabled': false,
    /**
     * Fail-safe default OFF — "Check" hidden from the Primary Payment Method
     * dropdown when LaunchDarkly is unavailable.
     */
    'admin.finance.payment-coordination-enable-check': false,
    /**
     * Fail-safe default OFF — "GROUP ACH" hidden from the Primary Payment
     * Method dropdown when LaunchDarkly is unavailable.
     */
    'admin.finance.payment-coordination-enable-group-ach': false,
    /**
     * Controls visibility of AP_CONFIRMED, PAYMENT_CLEARED, BOT_CLEARED in the
     * status update dropdown and bulk status CSV page.
     * Fail-safe default OFF — those statuses are hidden until turned on.
     */
    'admin.finance.payment-coordination-enable-clear-by-manual': false,
    /**
     * Controls the Finance > Invoice Management Stripe payment view.
     * Fail-safe default OFF — Delivery mode stays the plain delivery-only
     * view until LaunchDarkly turns it on.
     */
    'admin.finance.stripe-integration-enabled': false,
    /**
     * Client-side kill-switch for enrollment-platform RBAC enforcement.
     * Fail-safe default OFF — no provider means enforcement is disabled and
     * gates fall back to coarse role checks.
     */
    'admin.rbac-enforcement-enabled': false,
    /**
     * Controls visibility of the "Plan Benefits CSV" download/upload button
     * on the Plan Information (/plans-v2) page.
     */
    'admin.plan-management-plan-benefits-csv-enabled': false,
    /**
     * Controls visibility of the Upload tab inside the "Plan Benefits CSV"
     * modal — gated separately from the modal itself since Upload mutates
     * live data and Download doesn't.
     */
    'admin.plan-management-plan-benefits-csv-upload-enabled': false,
    // Fail-safe default OFF — demo management pages hidden when LaunchDarkly is unavailable.
    'admin.customers.demo-management-enabled': false,
    /**
     * Fail-safe default OFF — Document Center hidden from client HR admins
     * when LaunchDarkly is unavailable.
     */
    'admin.show-document-center-to-hr-admins': false,
    /**
     * Fail-safe default OFF — Health Plan tab Upload button disabled when
     * LaunchDarkly is unavailable, since upload mutates live data.
     */
    'admin.plan-management-v3-health-plan-upload-enabled': false,
  },
  /**
   * Callback methods used to perform actions on our feature flag storage, such as identifying a user after login.
   */
  methods: {
    /**
     * Identifies a user after a successful login.
     */
    identify: (_params: unknown) => {
      console.warn('No feature flag provider found.')
    },
  },
}
